import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ReduxState } from "../../../../redux";
import { Button, Col, Row, Alert } from "react-bootstrap";
import CategoriesPieChart from "./PieChart";
import { TagRegexes } from "./TagRegexes";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  fetchPieChart,
  fetchTagRegexes,
} from "../../../../redux/reducers/TagRegexes";
import { categorizeAction } from "../../../../redux/reducers/Categories";
import { AnalyzeTransactionsTable } from "../AnalyzeTransactionsTable";
import AgTable from "../AnalyzeTransactionsTable/AgTable";
import { useHistory } from "react-router-dom";
import ClipLoader from "react-spinners/ClipLoader";
import { categorizeActionAsync } from "../../../../redux/reducers/CategoriesAsync";
import { LoadingButton } from "../../../Docuclipper/TrackedButton";
// import SupersetDash from "./SupersetPieChart.tsx.disabled";
import AllAccountsSelector from "../AnalyzeTransactionsTable/AllAccountsSelector";

export const Categorize: React.FC = () => {
  const [dashboardReloadTrigger, setDashboardReloadTrigger] = useState(true);

  const dispatch = useDispatch();

  const {
    selectedTagRegexesSet,
    tagRegexesSets,
    loading: tagRegexesSetsLoading,
  } = useSelector((state: ReduxState) => state.TagRegexes);

  const { job } = useSelector((state: ReduxState) => state.JobData);
  const { categories, byCategoryKeyword } = useSelector(
    (state: ReduxState) => state.Categories
  );

  const { loading } = useSelector((state: ReduxState) => state.AsyncCategories);
  const { pieLoading } = useSelector((state: ReduxState) => state.TagRegexes);
  const { selectedAccountReconciler } = useSelector(
    (state: ReduxState) => state.Reconciler
  );

  const jobId = job ? job.id : "";

  const history = useHistory();
  const { selectedL1: storeL1, selectedL2: storeL2 } = useSelector(
    (state: ReduxState) => state.TagLevel
  );
  // Fallback to the URL's l1/l2 query params when the TagLevel slice hasn't
  // been populated yet (e.g. deep-link to /extractdata?...&mode=categorize
  // without having clicked through the TreeView first). Without this, the
  // pie-chart thunk bails because neither l1/l2 nor a job id are present.
  const { l1: urlL1, l2: urlL2 } = React.useMemo(() => {
    try {
      const sp = new URLSearchParams(history.location.search);
      return { l1: sp.get("l1") || "", l2: sp.get("l2") || "" };
    } catch {
      return { l1: "", l2: "" };
    }
  }, [history.location.search]);
  const l1 = storeL1 || urlL1;
  const l2 = storeL2 || urlL2;
  React.useEffect(() => {
    dispatch(fetchTagRegexes("categorize"));
  }, []);
  React.useEffect(() => {
    if (selectedAccountReconciler && l1 && l2) {
      dispatch(
        fetchPieChart({ selectedAccount: selectedAccountReconciler, l1, l2 })
      );
    }
  }, [selectedAccountReconciler, l1, l2]);

  React.useEffect(() => {
    if (selectedTagRegexesSet && categories.length === 0) {
      dispatch(categorizeAction());
    }
  }, []);

  // Applied to the transactions table when clicking Category Breakdown or Vendor Breakdown rows
  const [categoryFilterFromBreakdown, setCategoryFilterFromBreakdown] =
    useState<string | undefined>(undefined);
  const [categoryKeywordFilterFromBreakdown, setCategoryKeywordFilterFromBreakdown] =
    useState<string | undefined>(undefined);

  const [rowData, setRowData] = useState<{ name; value; color }[]>([]);
  React.useEffect(() => {
    setRowData(categories);
  }, [JSON.stringify(categories)]);

  const [columnDefs] = useState([
    {
      headerName: "Category",
      field: "name",
      filter: "agTextColumnFilter",
      flex: 2,
    },
    {
      headerName: "Amount $",
      field: "value",
      filter: "agNumberColumnFilter",
      flex: 1,
      sortable: true,
      sort: "desc" as const,
    },
  ]);

  useEffect(() => {
    setDashboardReloadTrigger(!loading);
  }, [loading]);

  // console.log({ categories, byCategoryKeyword });
  return (
    <div className="m-2">
      {tagRegexesSets.length === 0 && !tagRegexesSetsLoading && (
        <Alert variant="info">
          No category groups yet. Use "Edit Categories" to add one.
        </Alert>
      )}
      {tagRegexesSets.length > 0 && (
        <>
          <Row className="align-items-center">
            <Col md="6" data-tour-key="categorize-rules">
              <TagRegexes />
            </Col>
            <Col md="6">
              <div className="d-flex align-items-center justify-content-end flex-wrap gap-2">
                <AllAccountsSelector
                  onAccountChange={(newAccount) => {
                    dispatch(
                      fetchPieChart({
                        selectedAccount: newAccount,
                        l1,
                        l2,
                      })
                    );
                  }}
                />

                <LoadingButton
                  className="categorization-tour-categorize mx-2"
                  variant="primary"
                  isLoading={loading}
                  onClick={() => {
                    if (selectedTagRegexesSet) {
                      dispatch(categorizeActionAsync({ l1, l2 }));
                    }
                  }}
                >
                  {`Categorize ${
                    selectedAccountReconciler === "__ALL__"
                      ? " All Accounts"
                      : `${selectedAccountReconciler} Account`
                  }`}
                </LoadingButton>

              </div>
            </Col>
          </Row>

          {/* Uncategorized Transactions Warning - Moved to top for visibility */}
          {categories.length > 0 &&
            (() => {
              const uncategorizedData = categories.find(
                (cat) => cat.name === "Uncategorized"
              );
              const hasUncategorized =
                uncategorizedData && uncategorizedData.value !== 0;

              return hasUncategorized ? (
                <Row className="mt-3 mb-3" data-tour-key="categorize-uncategorized">
                  <Col md="12">
                    <Alert
                      variant="warning"
                      className="d-flex align-items-center"
                    >
                      <FontAwesomeIcon
                        icon="exclamation-triangle"
                        className="mr-2"
                      />
                      <div>
                        <strong>Uncategorized Transactions Detected</strong>
                        <span className="ml-2">
                          You have uncategorized transactions with a total value
                          of $
                          {Math.abs(uncategorizedData?.value || 0).toFixed(2)}.
                          Review them in the transactions table below.
                        </span>
                      </div>
                    </Alert>
                  </Col>
                </Row>
              ) : null;
            })()}

          {/* {false && (
            <div>
              <SupersetDash
                dashboardName={"categorizePieChart"}
                height={"400px"}
                width={"100%"}
                reload={dashboardReloadTrigger}
              />
            </div>
          )} */}
          {loading ? (
            <Row className="mt-4">
              <Col className="text-center">
                <ClipLoader size={50} />
                <div className="mt-3">
                  <h4>Categorization in progress...</h4>
                </div>
              </Col>
            </Row>
          ) : (
            categories.length > 0 && (
              <Row data-tour-key="categorize-charts">
                <Col md={6}>
                  <Row>
                    <Col className="mt-4">
                      <h3>Category Breakdown</h3>
                      {categories.length > 0 && (
                        <AgTable
                          className="categorization-tour-summary"
                          data-cy="categorization-table"
                          hasExport={true}
                          rowData={rowData}
                          columnDefs={columnDefs}
                          width={"100%"}
                          height={200}
                          rowHeight={28}
                          headerHeight={32}
                          onRowClicked={(e) => {
                            const name = e.data?.name;
                            if (name != null) {
                              setCategoryFilterFromBreakdown(name);
                              setCategoryKeywordFilterFromBreakdown(undefined);
                            }
                          }}
                          getRowStyle={() => ({ cursor: "pointer" })}
                        />
                      )}
                    </Col>
                    <Col style={{ height: "300px" }}>
                      {!pieLoading && <CategoriesPieChart data={categories} />}
                      {pieLoading && <ClipLoader />}
                    </Col>
                  </Row>
                </Col>

                <Col md={6}>
                  <Row>
                    <Col className="mt-4">
                      <h3>Vendor Breakdown</h3>
                      <AgTable
                        hasExport={true}
                        rowData={byCategoryKeyword}
                        columnDefs={[
                          {
                            headerName: "Keyword",
                            field: "name",
                            flex: 2,
                          },
                          {
                            headerName: "Amount $",
                            field: "value",
                            flex: 1,
                            sortable: true,
                            sort: "desc",
                          },
                        ]}
                        width={"100%"}
                        height={200}
                        rowHeight={28}
                        headerHeight={32}
                        onRowClicked={(e) => {
                          const name = e.data?.name;
                          if (name != null) {
                            setCategoryKeywordFilterFromBreakdown(name);
                            setCategoryFilterFromBreakdown(undefined);
                          }
                        }}
                        getRowStyle={() => ({ cursor: "pointer" })}
                      />
                    </Col>
                    <Col style={{ height: "300px" }}>
                      {!pieLoading && (
                        <CategoriesPieChart data={byCategoryKeyword} />
                      )}
                      {pieLoading && <ClipLoader />}
                    </Col>
                  </Row>
                </Col>
              </Row>
            )
          )}
        </>
      )}

      <div className="m-2">
        {loading && (
          <Alert variant="info" className="mb-3">
            <ClipLoader size={20} className="mr-2" />
            Categorization in progress... The table will update automatically when complete.
          </Alert>
        )}
        <AnalyzeTransactionsTable
          setDashboardReloadTrigger={setDashboardReloadTrigger}
          categoryFilterFromBreakdown={categoryFilterFromBreakdown}
          categoryKeywordFilterFromBreakdown={categoryKeywordFilterFromBreakdown}
          onClearCategoryBreakdownFilter={() =>
            setCategoryFilterFromBreakdown(undefined)
          }
        />
      </div>
    </div>
  );
};
export default Categorize;
